import { CloudUploadOutlined, InboxOutlined } from '@ant-design/icons';
import { Button, Col, DatePicker, message, Modal, Row, Upload, UploadFile } from 'antd';
import type { RangePickerProps } from 'antd/es/date-picker';
import { UploadProps } from 'antd/lib';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { CustomError } from '../../../../type';
import { useUploadBSPBillingFitsMutation } from '../api/fitsApisEndpoints';
import { UploadPdfData } from '../type/BSPType';

const { Dragger } = Upload;
const { RangePicker } = DatePicker;

interface Props {
  setResData?: React.Dispatch<React.SetStateAction<UploadPdfData[] | undefined>>;
  noModal?: boolean;
}

const UploadBSPBillingFits = ({ setResData, noModal }: Props) => {
  const navigate = useNavigate();
  const [upload, { isLoading, isError, isSuccess, data, error }] =
    useUploadBSPBillingFitsMutation();

  const CustomError = error as CustomError;

  const info = data?.data;

  useEffect(() => {
    setResData && setResData(info);
  }, [info]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [fileList, setFileList] = useState<UploadFile[]>();
  const [dates, setDates] = useState<{
    bsp_bill_start_date?: string;
    bsp_bill_end_date?: string;
    tbd_date?: string;
  }>({});

  const showModal = () => {
    setIsModalOpen(true);
  };

  const handleOk = () => {
    navigate('/reports/bsp-bill-list');
  };

  const submit = () => {
    const formData = new FormData();

    // Append files
    fileList?.map((item) => formData.append('file', item.originFileObj || ''));

    // Append dates
    if (dates.bsp_bill_start_date) {
      formData.append('bsp_bill_start_date', dates.bsp_bill_start_date);
    }
    if (dates.bsp_bill_end_date) {
      formData.append('bsp_bill_end_date', dates.bsp_bill_end_date);
    }
    if (dates.tbd_date) {
      formData.append('tbd_date', dates.tbd_date);
    }

    upload(formData);
  };

  const handleCancel = () => {
    setFileList([]);
    setDates({});
    setIsModalOpen(false);
  };

  const handleDateRangeChange: RangePickerProps['onChange'] = (dates, dateStrings) => {
    if (dates && dates.length === 2) {
      setDates((prev) => ({
        ...prev,
        bsp_bill_start_date: dateStrings[0],
        bsp_bill_end_date: dateStrings[1],
      }));
    }
  };

  const handleTbdDateChange = (date: any, dateString: any) => {
    setDates((prev) => ({
      ...prev,
      tbd_date: dateString,
    }));
  };

  const props: UploadProps = {
    name: 'file',
    multiple: true,
    beforeUpload: (file) => {
      const isExcel =
        [
          'application/vnd.ms-excel',
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          'application/vnd.ms-excel.sheet.macroEnabled.12',
          'application/vnd.ms-excel.addin.macroEnabled.12',
        ].includes(file.type) ||
        file.name.endsWith('.xls') ||
        file.name.endsWith('.xlsx');

      if (!isExcel) {
        message.error('Only Excel files are allowed (.xls, .xlsx)');
        return Upload.LIST_IGNORE; // This prevents the file from appearing in the fileList
      }
      return true;
    },
    maxCount: 10,
    fileList: fileList,
    accept: '.xls,.xlsx',
    onChange(info) {
      const { status } = info.file;

      // Filter out any non-excel files that might have slipped through
      const filteredFileList = info.fileList.filter((file: any) => {
        const isExcel =
          [
            'application/vnd.ms-excel',
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            'application/vnd.ms-excel.sheet.macroEnabled.12',
            'application/vnd.ms-excel.addin.macroEnabled.12',
          ].includes(file.type) ||
          file.name?.endsWith('.xls') ||
          file.name?.endsWith('.xlsx');

        return isExcel;
      });

      setFileList(filteredFileList);

      if (status === 'done') {
        message.success(`${info.file.name} file uploaded successfully.`);
      } else if (status === 'error') {
        // Check if the error is due to file type
        if (!filteredFileList.some((f) => f.uid === info.file.uid)) {
          message.error(`${info.file.name} is not a valid Excel file`);
        } else {
          message.error(`${info.file.name} file upload failed.`);
        }
      }
    },
    onDrop(e) {
      console.log('Dropped files', e.dataTransfer.files);
    },
  };

  useEffect(() => {
    if (isSuccess) {
      message.success('Pdf files uploaded');
      setFileList([]);
      setDates({});
    } else if (isError) {
      message.error(CustomError?.data?.message);
    }
  }, [isError, isSuccess, isError]);

  let jsx = <div>Loading ...</div>;

  if (noModal) {
    jsx = (
      <>
        <Row justify='center' gutter={[16, 16]}>
          <Col span={12}>
            <Dragger {...props} height={200}>
              <p className='ant-upload-drag-icon'>
                <InboxOutlined />
              </p>
              <p className='ant-upload-text'>Upload New BSP File</p>
              <p className='ant-upload-hint'>
                Support for a single upload: Upload BSP billing PDF document for checking
                calculation.
              </p>
            </Dragger>
          </Col>

          <Col span={12}>
            <Row gutter={[16, 16]}>
              <Col span={8}>
                <RangePicker
                  style={{ width: '100%' }}
                  onChange={handleDateRangeChange}
                  placeholder={['BSP Bill Start Date', 'BSP Bill End Date']}
                />
              </Col>
              <Col span={8}>
                <DatePicker
                  style={{ width: '100%' }}
                  onChange={handleTbdDateChange}
                  placeholder='TBD Date'
                />
              </Col>
              <Col span={8}>
                <Button
                  onClick={submit}
                  type='primary'
                  loading={isLoading}
                  style={{ width: '100%' }}
                >
                  Submit
                </Button>
              </Col>
            </Row>
          </Col>
        </Row>
      </>
    );
  } else {
    jsx = (
      <>
        <Button
          size='small'
          onClick={showModal}
          icon={<CloudUploadOutlined />}
          loading={isLoading}
        />
        <Modal
          title='Upload BSP Billing PDF Document'
          open={isModalOpen}
          onOk={handleOk}
          onCancel={handleCancel}
          destroyOnClose={true}
          confirmLoading={isLoading}
          footer={[
            <Button key='back' onClick={handleCancel}>
              Cancel
            </Button>,
            <Button
              key='submit'
              type='primary'
              onClick={submit}
              loading={isLoading}
              disabled={!fileList?.length || !dates.bsp_bill_start_date || !dates.bsp_bill_end_date}
            >
              Submit
            </Button>,
          ]}
        >
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Row gutter={16}>
                <Col span={12}>
                  <RangePicker
                    style={{ width: '100%' }}
                    onChange={handleDateRangeChange}
                    placeholder={['BSP Bill Start Date', 'BSP Bill End Date']}
                  />
                </Col>
                <Col span={12}>
                  <DatePicker
                    style={{ width: '100%' }}
                    onChange={handleTbdDateChange}
                    placeholder='TBD Date'
                  />
                </Col>
              </Row>
            </Col>
            <Col span={24}>
              <Dragger {...props}>
                <p className='ant-upload-drag-icon'>
                  <InboxOutlined />
                </p>
                <p className='ant-upload-text'>Click or drag file to this area to upload</p>
                <p className='ant-upload-hint'>
                  Support for a single upload: Upload BSP billing document for checking calculation.
                </p>
              </Dragger>
            </Col>
          </Row>
        </Modal>
      </>
    );
  }

  return jsx;
};

export default UploadBSPBillingFits;
